import { useCallback, useEffect, useState } from "react";

import { useDebounce } from "../../../hooks";
import { SportPlay } from "../../../types";
import { API, DEFAULT_PLAY_PAGE_SIZE, MEDIA_SERVER, X_NUM_OF_PAGES, X_PAGE, X_TOTAL_ROWS } from "../../constants";
import { ApiResponse, PagedResponse } from "../../types";
import { apiSlice } from "../api-slice";
import {
  EditPlayQueryParams,
  GetPlayQueryParams,
  GetSinglePlayParams,
  PlayPayload,
  VideoAssetInitResponse,
} from "./types";
import { convertSportPlayToPlayPayload } from "./utils";

const getPlayFilterQueryString = ({
  page = 0,
  pageSize = DEFAULT_PLAY_PAGE_SIZE,
  fieldSearch = [],
  sort,
}: GetPlayQueryParams) => {
  const pageStringified = `&page=${page.toString()}`;
  const pageSizeStringified = `&size=${pageSize.toString()}`;
  const fieldSearchStringified = `${fieldSearch.length > 0 ? "&" : ""}${fieldSearch
    .map((search) => `${search.name}=${search.value}`)
    .join("&")}`;

  const sortStringified = sort ? `&sort=${sort.field}:${sort.order}` : "";

  return `${pageStringified}${pageSizeStringified}${fieldSearchStringified}${sortStringified}`;
};

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /** ======== Get Filtered Plays ======== */
    getFilteredPlays: build.query<PagedResponse<PlayPayload[]>, GetPlayQueryParams>({
      query: ({ page = 0, pageSize = DEFAULT_PLAY_PAGE_SIZE, fieldSearch = [], sort }) => {
        const queryString = getPlayFilterQueryString({ page, pageSize, fieldSearch, sort });
        return `${API}/plays/search?type=play&includeFrames=false${queryString}`;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return getPlayFilterQueryString(queryArgs);
      },
      transformResponse: (response: ApiResponse<PlayPayload[]>, meta) => {
        const headers = meta?.response?.headers;
        const page = Number(headers?.get(X_PAGE)) || 0;
        const numOfPages = Number(headers?.get(X_NUM_OF_PAGES)) || 0;
        const totalRows = Number(headers?.get(X_TOTAL_ROWS)) || 0;

        return { page, numOfPages, totalRows, data: response.data };
      },
      providesTags: (result) =>
        result
          ? // successful query
            [...result.data.map(({ id }) => ({ type: "PlayData", id } as const)), { type: "PlayData", id: "LIST" }]
          : // an error occurred, but we still want to refetch this query when `{ type: "PlayData", id: "LIST" }` is
            // invalidated
            [{ type: "PlayData", id: "LIST" }],
    }),
    /** ======== Get Single Play ======== */
    getSinglePlay: build.query<PlayPayload, GetSinglePlayParams>({
      query: ({ id }) => {
        return `${API}/plays/playsWithFields/${id}`;
      },
      providesTags: (result, error, { id }) => [{ type: "PlayData", id }],
    }),

    deletePlays: build.mutation<ApiResponse<{}>, { ids: string[] }>({
      query: ({ ids }) => ({ method: "PUT", url: `${API}/plays/delete`, body: { playIds: ids } }),
      invalidatesTags: ["PlayData"],
      // onQueryStarted: async ({ ids }, { dispatch, queryFulfilled }) => {
      //   const result = dispatch(
      //     extendedApiSlice.util.updateQueryData(
      //       "getFilteredPlays",
      //       { page: 0, pageSize: DEFAULT_PLAY_PAGE_SIZE, fieldSearch: [] },
      //       (draft) => {
      //         return draft.data.filter((play) => !ids.includes(play?.id || "")) as any;
      //       }
      //     )
      //   );

      //   try {
      //     await queryFulfilled;
      //   } catch {
      //     result.undo();
      //   }
      // },
    }),

    createPlay: build.mutation<PlayPayload, { play: SportPlay }>({
      query: ({ play }) => ({ method: "POST", url: `${API}/plays`, body: convertSportPlayToPlayPayload(play) }),
      invalidatesTags: ["PlayData"],
      transformResponse: (response: ApiResponse<PlayPayload>) => response.data,
    }),

    editPlay: build.mutation<ApiResponse<{}>, EditPlayQueryParams>({
      query: ({ play, fromDone = true }) => ({
        method: "POST",
        url: `${API}/plays?done=${fromDone}`,
        body: convertSportPlayToPlayPayload(play),
      }),
      invalidatesTags: (_result, _error, { play }) => [{ type: "PlayData", id: play.id }],
      // onQueryStarted: async ({ play }, { dispatch, queryFulfilled }) => {
      //   const patchResult = dispatch(
      //     extendedApiSlice.util.updateQueryData(
      //       "getFilteredPlays",
      //       {
      //         page: 0,
      //         pageSize: DEFAULT_PLAY_PAGE_SIZE,
      //         fieldSearch: [],
      //         sort: { field: "updatedTimestamp", order: "DESC" },
      //       },
      //       (draft) => {
      //         const indexToUpdate = draft.data.findIndex((playData) => playData.id === play.id);
      //         draft.data.splice(indexToUpdate, 1, play);
      //       }
      //     )
      //   );

      //   try {
      //     await queryFulfilled;
      //   } catch {
      //     patchResult.undo();
      //   }
      // },
    }),

    duplicatePlay: build.mutation<PlayPayload, { playId: string }>({
      query: ({ playId }) => ({ method: "POST", url: `${API}/plays?duplicate=true`, body: { id: playId } }),
      invalidatesTags: ["PlayData"],
      transformResponse: (response: ApiResponse<PlayPayload>) => response.data,
    }),

    savePlayOptimistically: build.mutation<ApiResponse<{}>, { play: SportPlay }>({
      query: ({ play }) => ({ method: "POST", url: `${API}/plays`, body: convertSportPlayToPlayPayload(play) }),
      onQueryStarted: async ({ play }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          extendedApiSlice.util.updateQueryData("getSinglePlay", { id: play.id || "" }, (draft) => {
            Object.assign(draft, play);
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    bulkChangeTeams: build.mutation<ApiResponse<{}>, { playIds: string[]; teamId: string }>({
      query: ({ playIds, teamId }) => ({
        method: "PUT",
        url: `${API}/plays/movetootherteam`,
        body: { playIds, teamId, type: "move" },
      }),
      invalidatesTags: ["PlayData"],
    }),
    bulkChangeSeries: build.mutation<ApiResponse<{}>, { playIds: string[]; seriesId: string }>({
      query: ({ playIds, seriesId }) => ({
        method: "PUT",
        url: `${API}/plays/movetootherseries`,
        body: { playIds, seriesId },
      }),
      invalidatesTags: ["PlayData"],
    }),

    updatePlaysRecipients: build.mutation<ApiResponse<{}>, { playIds: string[]; recipients: string[] }>({
      query: ({ playIds, recipients }) => ({
        method: "PUT",
        url: `${API}/plays/recipients`,
        body: { selectedIds: recipients, playIds },
      }),
      invalidatesTags: ["PlayData"],
    }),

    patchPlays: build.mutation<ApiResponse<{}>, { plays: SportPlay[] }>({
      query: ({ plays }) => ({
        method: "PATCH",
        url: `${API}/plays`,
        body: plays.map(convertSportPlayToPlayPayload),
      }),
      invalidatesTags: ["PlayData"],
    }),

    initializePlayVideo: build.mutation<
      VideoAssetInitResponse[],
      {
        playId: string;
        duration: number;
        identifier: string;
        length: number;
        playbackOut: number;
        size: number;
        title: string;
      }
    >({
      query: ({ playId, duration, identifier, title, length, size, playbackOut }) => ({
        method: "POST",
        url: `${API}/plays/${playId}/assets/initialize`,
        body: [
          {
            duration,
            identifier,
            length,
            playbackOut,
            title,
            size,
            playbackIn: 0,
            appKey: "FASTDRAW",
            description: "",
            order: 0,
            source: "media-service",
            uploadComplete: false,
          },
        ],
      }),
    }),

    updatePlayVideo: build.mutation<ApiResponse<{}>, { playId: string; assetId: string; body: any }>({
      query: ({ playId, assetId, body }) => ({
        method: "PUT",
        url: `${API}/plays/${playId}/assets/${assetId}`,
        body,
      }),
      invalidatesTags: (_result, _error, { playId }) => [{ type: "PlayData", id: playId }],
    }),

    deletePlayVideo: build.mutation<ApiResponse<{}>, { playId: string; assetId: string }>({
      query: ({ playId, assetId }) => ({
        method: "DELETE",
        url: `${API}/plays/${playId}/assets/${assetId}`,
      }),
      invalidatesTags: (_result, _error, { playId }) => [{ type: "PlayData", id: playId }],
    }),

    attachPlayVideo: build.mutation<ApiResponse<{}>, { playId: string; body: any }>({
      query: ({ playId, body }) => ({
        method: "POST",
        url: `${API}/plays/${playId}/assets`,
        body,
      }),
      invalidatesTags: (_result, _error, { playId }) => [{ type: "PlayData", id: playId }],
    }),

    uploadPlayVideo: build.mutation<
      ApiResponse<{}>,
      {
        formData: FormData;
        playId: string;
      }
    >({
      query: ({ formData }) => ({
        method: "POST",
        url: `${MEDIA_SERVER}upload`,
        body: formData,
        formData: true,
      }),
      invalidatesTags: (_result, _error, { playId }) => [{ type: "PlayData", id: playId }],
    }),
  }),
});

export const {
  endpoints,
  useGetFilteredPlaysQuery,
  useLazyGetFilteredPlaysQuery,
  useGetSinglePlayQuery,
  useLazyGetSinglePlayQuery,
  useDeletePlaysMutation,
  useCreatePlayMutation,
  useSavePlayOptimisticallyMutation,
  useBulkChangeTeamsMutation,
  useBulkChangeSeriesMutation,
  useDuplicatePlayMutation,
  useInitializePlayVideoMutation,
  useUploadPlayVideoMutation,
  useDeletePlayVideoMutation,
  useAttachPlayVideoMutation,
  useUpdatePlaysRecipientsMutation,
  usePatchPlaysMutation,
  useUpdatePlayVideoMutation,
} = extendedApiSlice;

const { useEditPlayMutation: useEditPlayMutationInternal } = extendedApiSlice;

export const useEditPlayMutation = () => {
  const [editPlay, editPlayProps] = useEditPlayMutationInternal();
  const [editPlayQuery, setEditPlayQuery] = useState<EditPlayQueryParams>();
  const debouncedPlay = useDebounce(editPlayQuery, 1000);

  const editPlayDebounced = useCallback(async ({ play, fromDone = false }: EditPlayQueryParams) => {
    setEditPlayQuery({ play, fromDone });
  }, []);

  useEffect(() => {
    if (debouncedPlay) {
      const { play, fromDone } = debouncedPlay;
      editPlay({ play, fromDone });
    }
  }, [debouncedPlay, editPlay]);

  return { editPlay, editPlayDebounced, editPlayProps };
};
